import { mergeAttributes, Node } from "@tiptap/core";

import type { NodeJsonSerializer } from "../utils/types";
import type { NodeJson } from "@corca/data";
import type { DOMOutputSpec } from "@tiptap/pm/model";

export type InlineNodeAttrs = {
  nodeId: string;
  width?: number | null;
  height?: number | null;
  verticalOffset?: number | null;
  indentation?: number | null;
  indentNodeLeftOffset?: number | null;
  indentNodeRightOffset?: number | null;
  automaticIndentation?: boolean | null;
};

export type InlineExpressionOptions = {
  getNodeJson: (id: string) => NodeJson | undefined;
  serializeNode: NodeJsonSerializer;
  /**
   * When true, renderHTML includes the expression as child content: what `renderNodeContent`
   * builds, or else the serialized text. This should only be set in SSR/schema contexts
   * (e.g. serializeYDocToHtml), not in the client-side editor, to avoid polluting getHTML() output.
   */
  renderAsHtml: boolean;
  /**
   * Markup for an expression under `renderAsHtml`, a DOM node or a nested output spec, in place of
   * `serializeNode`'s text. Returning `undefined` falls back to the text.
   */
  renderNodeContent?: (node: NodeJson) => DOMOutputSpec | undefined;
};

function parseNumericAttr(el: HTMLElement, attr: string): number | null {
  const val = el.getAttribute(attr);
  if (val == null) return null;
  const num = Number(val);
  return Number.isNaN(num) ? null : num;
}

export const InlineExpression = Node.create<InlineExpressionOptions>({
  name: "inlineExpression",

  group: "inline",

  atom: true,

  inline: true,

  addAttributes() {
    return {
      nodeId: {
        default: 0,
      },
      nodesJson: {
        default: null,
      },
      isInline: {
        default: true,
      },
      width: {
        default: null,
        parseHTML: (el) => parseNumericAttr(el, "width"),
      },
      height: {
        default: null,
        parseHTML: (el) => parseNumericAttr(el, "height"),
      },
      verticalOffset: {
        default: null,
        parseHTML: (el) => parseNumericAttr(el, "verticalOffset"),
      },
      indentation: {
        default: null,
        parseHTML: (el) => parseNumericAttr(el, "indentation"),
      },
      indentNodeLeftOffset: {
        default: null,
        parseHTML: (el) => parseNumericAttr(el, "indentNodeLeftOffset"),
      },
      indentNodeRightOffset: {
        default: null,
        parseHTML: (el) => parseNumericAttr(el, "indentNodeRightOffset"),
      },
      automaticIndentation: {
        default: null,
        renderHTML: () => ({}),
      },
      source: {
        default: null,
      },
    };
  },

  renderText({ node, parent, pos }) {
    const { getNodeJson, serializeNode } = this.options;
    const nodeId = node.attrs.nodeId as string;
    const nodeJson = getNodeJson?.(nodeId);
    if (!nodeJson || !this.editor) {
      return "";
    }

    return serializeNode?.(nodeJson, node, this.editor.state.doc, parent, pos) ?? "";
  },

  parseHTML() {
    return [
      {
        tag: "inline-expression",
      },
      {
        tag: 'span[data-type="inline-expression"]',
        getAttrs: (el) => {
          if (typeof el === "string") return false;
          return {
            nodeId: el.getAttribute("data-node-id"),
            nodesJson: el.getAttribute("data-nodes-json"),
            isInline: el.getAttribute("data-is-inline") !== "false",
          };
        },
      },
    ];
  },

  renderHTML({ node, HTMLAttributes }) {
    const { getNodeJson, serializeNode, renderNodeContent, renderAsHtml } = this.options;

    if (renderAsHtml) {
      const nodeId = node.attrs.nodeId as string;
      const nodeJson = getNodeJson?.(nodeId);

      if (nodeJson) {
        const content =
          renderNodeContent?.(nodeJson) ?? serializeNode?.(nodeJson, node, node, null, 0);
        if (content) {
          return ["inline-expression", mergeAttributes(HTMLAttributes), content];
        }
      }
    }

    return ["inline-expression", mergeAttributes(HTMLAttributes)];
  },
});
